import { FeatureFlags } from 'common/feature_flags';
import { checkStatus, defaultHeaders } from 'common/http';
import { isBlob, isHref } from 'common/views/view_types';
import { View } from 'common/types/view';
import { get, isString, toNumber, isNaN } from 'lodash';
import { Nullable } from 'common/types/nullable';

/**
 * Shared utility function to fetch row count from API.
 *
 * This implements the same logic as fetchRowCount in datasetLandingPage/actions/view.js
 * but as a pure function that can be used by both Redux actions and React providers.
 *
 * @param viewId - The view ID (fourfour) to fetch row count for
 * @param viewForTypeCheck - Optional view object for blob/href type checking
 * @param queryClause - Optional query clause for filtered row counts (e.g., "WHERE age > 18")
 * @returns Promise<Nullable<number>> - The row count, 0 if not applicable, or null if invalid response
 */
export const fetchRowCountFromAPI = async (
  viewId: string,
  viewForTypeCheck?: Pick<View, 'id'> & Partial<View>,
  queryClause?: string
): Promise<Nullable<number>> => {
  // Check if this is a blob or href view (no row count)
  // Use provided view object if available, otherwise create minimal one for type checking
  const viewToCheck = viewForTypeCheck || { id: viewId };
  const noRowCountConditions = [isBlob(viewToCheck as View), isHref(viewToCheck as View)];

  if (noRowCountConditions.some(Boolean)) {
    return 0; // This view does not have rows
  }

  const fetchOptions: RequestInit = {
    method: 'GET',
    headers: {
      ...defaultHeaders,
      'X-Socrata-Federation': 'Honey Badger'
    },
    credentials: 'same-origin'
  };

  // Use the same URL logic as the original fetchRowCount function
  const urlPath = FeatureFlags.value('soda3_dsop')
    ? `/api/v3/views/${viewId}/query.json?`
    : `/api/id/${viewId}?$`;

  // Build the count query with optional WHERE clause
  const baseQuery = 'select count(*) as COLUMN_ALIAS_GUARD__count';
  const fullQuery = queryClause ? `${baseQuery} WHERE ${queryClause}` : baseQuery;
  const url = `${urlPath}query=${encodeURIComponent(fullQuery)}`;

  const response = await fetch(url, fetchOptions);
  checkStatus(response);
  const apiResponse = await response.json();

  // Parse response using same logic as original (handles both OBE and NBE)
  // OBE will lowercase the field in the response.
  const rowCountOBE = get(apiResponse, '[0].column_alias_guard__count', null);
  const rowCountNBE = get(apiResponse, '[0].COLUMN_ALIAS_GUARD__count', null);

  let rowCount = null;
  if (isString(rowCountNBE)) {
    rowCount = toNumber(rowCountNBE);
  } else if (isString(rowCountOBE)) {
    rowCount = toNumber(rowCountOBE);
  }

  // Return null for invalid responses, 0 for valid responses with no rows
  return rowCount !== null ? (isNaN(rowCount) ? null : rowCount) : null;
};
